import { call, SagaGenerator, select, takeLatest } from "typed-redux-saga";
import { Action, AnyAction } from "typescript-fsa";

import { ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Events, ModelSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import {
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { DocumentServiceFactory } from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { RESOURCE_KEYS } from "../../localization";
import { isWorkflowActivity, saveActivity } from "../../utilities/activityUtils";
import { putErrorNotification } from "../../utilities/notificationUtils";
import { archive } from "../../utilities/rpcHelper";
import { executeWithoutValidation, weakValidation } from "../../utilities/validationUtils";
import {
	VERAKTUNG_CONTENT_TYPE,
	VERAKTUNG_LOCATION,
	WEAK_VALIDATION_IDENTIFIERS
} from "../../utilities/variables/modulfRuntimeAnnotations";
import { executeBusy, updateWorkflowsActivityData } from "../../utilities/workflowUtils";
import { FormModelUtils } from "../../workaround/internal/form-model-utils";

const documentService = new DocumentServiceFactory().getDocumentService();

export function* archiveAntragSaga(): SagaGenerator<void> {
	yield* takeLatest((action: AnyAction) => {
		return (
			FormEngineActions.event.match(action) &&
			Events.eventButtonTriggered.match(action.payload.engineEvent) &&
			["archive", "archive_pdf"].includes(action.payload.engineEvent.payload.name)
		);
	}, handle);
}

function* handle(action: Action<FormEngineActions.FormEngineEventActions>): SagaGenerator<void> {
	yield* call(executeBusy, function* (): SagaGenerator<void> {
		const activityId = action.payload.activityId;

		const activity = yield* select(ActivitySelectors.activityById(activityId));
		if (!activity || !isWorkflowActivity(activity)) {
			yield* call(handleError, `Activity ${activityId} not found.`);
			return;
		}

		const group = `archiveAntrag:${activity.descriptor.instance}`;
		const includeWeakModules = [WEAK_VALIDATION_IDENTIFIERS.GEBUEHRENFORDERUNG];
		const isValid = yield* call(weakValidation, activity.id, includeWeakModules);
		if (!isValid) {
			handleError("Document is not valid.", group);
			return;
		}

		const engineState = yield* select(FormEngineSelectors.engineState(activityId));
		if (!engineState) {
			yield* call(handleError, "Couldn't get EngineState", group);
			return;
		}

		const pathElements = FormModelUtils.findPathElementsByFormModelPath(
			ModelSelectors.formModel()(engineState),
			action.payload.engineEvent.payload.buttonPath
		);
		const eventButton = pathElements[pathElements.length - 1];
		if (!FormModelUtils.isAnnotatedInstance(eventButton)) {
			yield* call(handleError, "No annotations on button found.", group);
			return;
		}

		const docModel = ModelSelectors.documentModel()(engineState);

		const documentReference = activity.descriptor.instance;
		if (!documentReference) {
			yield* call(handleError, `No document reference for ${activityId} found.`, group);
			return;
		}

		const isSaved: boolean = yield* call(
			executeWithoutValidation<boolean>,
			activityId,
			function* (): SagaGenerator<boolean> {
				return yield* call(saveActivity, activityId);
			}
		);

		if (!isSaved) {
			yield* call(handleError, "Saving failed for activity " + activityId, group);
			return;
		}

		let pathToArchiveGroup = eventButton?.annotations?.find(e => e.name === VERAKTUNG_LOCATION)?.value;
		if (!pathToArchiveGroup) {
			yield* call(handleError, `Annotation ${VERAKTUNG_LOCATION} not found.`, group);
			return;
		}

		pathToArchiveGroup = "/" + pathToArchiveGroup.split("/").slice(1).join("/");
		//Use CSV as Fallback to prevent migration
		const archiveContentType =
			eventButton?.annotations?.find(e => e.name === VERAKTUNG_CONTENT_TYPE)?.value ?? "CSV";
		const updatedDocumentSpec = yield* call(archive, documentReference, pathToArchiveGroup, archiveContentType);
		const documentWithArchive = {
			...documentService.parseDates(updatedDocumentSpec.document, docModel),
			modelId: docModel.header.id,
			id: documentReference
		};

		yield* call(updateWorkflowsActivityData, { ...documentWithArchive, id: documentReference });
	});
}

function* handleError(error: string, group?: string) {
	yield* call(putErrorNotification, RESOURCE_KEYS.features.notification.error.archive, error, undefined, group);
}
